import { debounce } from './debounce';
import { initSuggestionsScrollbar, updateSuggestionsScrollbar } from './suggestions-scrollbar';

export interface ArticleSearchSuggestion {
  title: string;
  url: string;
}

export interface ArticleSearchAutocompleteOptions {
  root: HTMLElement;
  input: HTMLInputElement;
  suggestionsList: HTMLUListElement;
  apiUrl?: string;
  debounceMs?: number;
  minQueryLength?: number;
  maxSuggestions?: number;
}

const DEFAULT_API_URL = '/api/articles/search';
const DEFAULT_DEBOUNCE_MS = 300;
const DEFAULT_MIN_QUERY_LENGTH = 1;
const DEFAULT_MAX_SUGGESTIONS = 8;

function resolveArticleUrl(url: string): string {
  const path = url.replace(/^~\/?/, '/');
  return new URL(path, window.location.origin).toString();
}

function normalizeSuggestion(item: unknown): ArticleSearchSuggestion | null {
  if (!item || typeof item !== 'object') return null;

  const record = item as Record<string, unknown>;
  const title = record.title ?? record.Title;
  const url = record.url ?? record.Url;

  if (typeof title !== 'string' || typeof url !== 'string' || !title || !url) {
    return null;
  }

  return { title, url };
}

function normalizeSuggestions(data: unknown): ArticleSearchSuggestion[] {
  if (!Array.isArray(data)) return [];

  return data
    .map(normalizeSuggestion)
    .filter((item): item is ArticleSearchSuggestion => item !== null);
}

function getMockSuggestions(root: HTMLElement): ArticleSearchSuggestion[] | null {
  if (root.dataset.searchUseMock !== 'true') return null;

  const dataElement = root.querySelector<HTMLScriptElement>('script[data-search-mock-suggestions]');
  if (!dataElement?.textContent) return [];

  try {
    return normalizeSuggestions(JSON.parse(dataElement.textContent));
  } catch {
    return [];
  }
}

function filterSuggestionsByQuery(
  items: ArticleSearchSuggestion[],
  query: string,
): ArticleSearchSuggestion[] {
  const normalizedQuery = query.toLowerCase();

  return items.filter((item) => item.title.toLowerCase().includes(normalizedQuery));
}

export function initArticleSearchAutocomplete({
  root,
  input,
  suggestionsList,
  apiUrl = root.dataset.searchApiUrl || DEFAULT_API_URL,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  minQueryLength = DEFAULT_MIN_QUERY_LENGTH,
  maxSuggestions = DEFAULT_MAX_SUGGESTIONS,
}: ArticleSearchAutocompleteOptions): void {
  const suggestionsPanel = suggestionsList.closest<HTMLElement>('.header-with-search__suggestions-panel');
  const mockSuggestions = getMockSuggestions(root);
  const keepSuggestionsOpen = root.dataset.searchKeepOpen === 'true';
  let activeIndex = -1;
  let abortController: AbortController | null = null;
  let closeTimeout = 0;
  let suggestions: ArticleSearchSuggestion[] = [];

  const suggestionId = suggestionsList.id || `search-suggestions-${Math.random().toString(36).slice(2, 9)}`;
  suggestionsList.id = suggestionId;
  input.setAttribute('autocomplete', 'off');
  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-autocomplete', 'list');
  input.setAttribute('aria-controls', suggestionId);
  input.setAttribute('aria-expanded', 'false');

  function setExpanded(isOpen: boolean): void {
    input.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  }

  function setSuggestionsPanelVisible(isVisible: boolean): void {
    if (suggestionsPanel) {
      suggestionsPanel.hidden = !isVisible;
      return;
    }

    suggestionsList.hidden = !isVisible;
  }

  function hideSuggestions(): void {
    if (keepSuggestionsOpen) return;

    suggestions = [];
    activeIndex = -1;
    suggestionsList.innerHTML = '';
    setSuggestionsPanelVisible(false);
    setExpanded(false);
    input.removeAttribute('aria-activedescendant');
  }

  function renderSuggestions(items: ArticleSearchSuggestion[]): void {
    suggestions = items.slice(0, maxSuggestions);
    activeIndex = -1;
    suggestionsList.innerHTML = '';

    if (!suggestions.length) {
      hideSuggestions();
      return;
    }

    suggestions.forEach((item, index) => {
      const li = document.createElement('li');
      li.className = 'header-with-search__suggestion';
      li.setAttribute('role', 'option');
      li.id = `${suggestionId}-option-${index}`;

      const link = document.createElement('a');
      link.className = 'header-with-search__suggestion-link';
      link.href = resolveArticleUrl(item.url);
      link.textContent = item.title;

      li.appendChild(link);
      suggestionsList.appendChild(li);
    });

    setSuggestionsPanelVisible(true);
    setExpanded(true);

    if (suggestionsPanel) {
      initSuggestionsScrollbar(suggestionsPanel);
      window.requestAnimationFrame(() => updateSuggestionsScrollbar(suggestionsPanel));
    }
  }

  function highlightSuggestion(index: number): void {
    const options = suggestionsList.querySelectorAll<HTMLElement>('[role="option"]');
    options.forEach((option, optionIndex) => {
      const isActive = optionIndex === index;
      option.classList.toggle('header-with-search__suggestion--active', isActive);
      if (isActive) {
        input.setAttribute('aria-activedescendant', option.id);
      }
    });

    if (index < 0) {
      input.removeAttribute('aria-activedescendant');
    }
  }

  async function fetchSuggestions(query: string): Promise<void> {
    if (mockSuggestions) {
      renderSuggestions(filterSuggestionsByQuery(mockSuggestions, query));
      return;
    }

    abortController?.abort();
    abortController = new AbortController();

    const url = new URL(apiUrl, window.location.origin);
    url.searchParams.set('q', query);

    try {
      const response = await fetch(url.toString(), { signal: abortController.signal });

      if (!response.ok) {
        hideSuggestions();
        return;
      }

      const data = await response.json();
      renderSuggestions(normalizeSuggestions(data));
    } catch (error) {
      if ((error as DOMException).name !== 'AbortError') {
        hideSuggestions();
      }
    }
  }

  const debouncedFetch = debounce((query: string) => {
    void fetchSuggestions(query);
  }, debounceMs);

  function handleInput(): void {
    const query = input.value.trim();

    if (query.length < minQueryLength) {
      abortController?.abort();

      if (keepSuggestionsOpen && mockSuggestions) {
        renderSuggestions(mockSuggestions);
        return;
      }

      hideSuggestions();
      return;
    }

    debouncedFetch(query);
  }

  function openActiveSuggestion(): void {
    if (activeIndex < 0 || !suggestions[activeIndex]) return;

    window.location.href = resolveArticleUrl(suggestions[activeIndex].url);
  }

  input.addEventListener('input', handleInput);

  input.addEventListener('keydown', (event) => {
    if (!suggestions.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      activeIndex = Math.min(activeIndex + 1, suggestions.length - 1);
      highlightSuggestion(activeIndex);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      activeIndex = Math.max(activeIndex - 1, 0);
      highlightSuggestion(activeIndex);
      return;
    }

    if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      openActiveSuggestion();
      return;
    }

    if (event.key === 'Escape' && !keepSuggestionsOpen) {
      hideSuggestions();
    }
  });

  if (!keepSuggestionsOpen) {
    input.addEventListener('blur', () => {
      window.clearTimeout(closeTimeout);
      closeTimeout = window.setTimeout(() => hideSuggestions(), 150);
    });

    root.addEventListener('focusout', (event) => {
      const nextTarget = event.relatedTarget as Node | null;
      if (nextTarget && root.contains(nextTarget)) return;
      hideSuggestions();
    });
  }

  suggestionsList.addEventListener('mousedown', (event) => {
    event.preventDefault();
  });

  if (keepSuggestionsOpen && mockSuggestions) {
    const query = input.value.trim();
    renderSuggestions(query ? filterSuggestionsByQuery(mockSuggestions, query) : mockSuggestions);
  }
}
